// src/LoginPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { User, Lock, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";

const API_BASE = process.env.REACT_APP_API_BASE;
const APP_MODE = String(process.env.REACT_APP_MODE || "").toLowerCase();
const isDevMode = APP_MODE === "dev" || process.env.NODE_ENV !== "production";
const hideExternalDigitalLogin =
  String(process.env.REACT_APP_HIDE_EXTERNAL_DIGITAL_LOGIN || "").toLowerCase() === "true";
const shouldHideDigitalIdButtons = isDevMode && hideExternalDigitalLogin;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessedThaiIdCallback = useRef(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focusedInput, setFocusedInput] = useState(null);

  const generateRandomState = (length = 32) => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

    if (window.crypto?.getRandomValues) {
      const randomValues = new Uint8Array(length);
      window.crypto.getRandomValues(randomValues);
      return Array.from(randomValues, (value) => chars[value % chars.length]).join("");
    }

    return Array.from(
      { length },
      () => chars[Math.floor(Math.random() * chars.length)]
    ).join("");
  };

  const handleThaiIdLogin = () => {
    const randomState = generateRandomState(32);
    const application = "aihub";
    const os = "webapp";
    const fullState = `${randomState}-${application}-${os}`;
    const oauthUrl = new URL(
      "https://imauth.bora.dopa.go.th/api/v2/oauth2/auth/"
    );

    // เก็บ state ไว้ฝั่ง browser เพื่อใช้ verify ตอน callback
    sessionStorage.setItem("thaid_oauth_state", randomState);
    sessionStorage.setItem(
      "thaid_oauth_state_expires_at",
      String(Date.now() + 5 * 60 * 1000)
    );
    // เก็บซ้ำใน localStorage เผื่อ mobile browser เปลี่ยน session ระหว่าง redirect
    localStorage.setItem("thaid_oauth_state", randomState);
    localStorage.setItem(
      "thaid_oauth_state_expires_at",
      String(Date.now() + 15 * 60 * 1000)
    );

    oauthUrl.searchParams.set("response_type", "code");
    oauthUrl.searchParams.set(
      "client_id",
      "TkU5NXExVWVuVnFxMGF5ZjFrR0h4b1FFblRySFl6VWw"
    );
    oauthUrl.searchParams.set(
      "redirect_uri",
      "https://imis.psru.ac.th/thaid/callback.php"
    );
    oauthUrl.searchParams.set("scope", "pid openid");
    oauthUrl.searchParams.set("state", fullState);

    window.location.assign(oauthUrl.toString());
  };

  const handlePsruNextLogin = () => {
    if (!API_BASE) {
      setError("ยังไม่ได้ตั้งค่า REACT_APP_API_BASE");
      return;
    }
    // API_BASE อาจเป็น relative path เช่น /api (production) — ต้องมี base
    const startUrl = new URL(
      `${API_BASE.replace(/\/$/, "")}/auth/psrunext/start`,
      window.location.origin
    );
    window.location.assign(startUrl.toString());
  };

  useEffect(() => {
    if (hasProcessedThaiIdCallback.current) return;

    const params = new URLSearchParams(window.location.search);
    const thaidCode = (params.get("thaid_code") || "").trim();
    const callbackState = (params.get("state") || "").trim();
    if (!thaidCode || !callbackState) return;

    hasProcessedThaiIdCallback.current = true;

    const processThaiIdCallback = async () => {
      setError("");
      setLoading(true);

      try {
        if (!API_BASE) {
          throw new Error("ยังไม่ได้ตั้งค่า REACT_APP_API_BASE");
        }

        const [randomFromState, application, os] = callbackState.split("-", 3);
        const expectedRandomState = (
          sessionStorage.getItem("thaid_oauth_state") ||
          localStorage.getItem("thaid_oauth_state") ||
          ""
        ).trim();

        if (
          !randomFromState ||
          application !== "aihub" ||
          os !== "webapp"
        ) {
          throw new Error(
            "State ของ ThaID ไม่ถูกต้องหรือหมดอายุ กรุณาเริ่มเข้าสู่ระบบใหม่"
          );
        }
        // ตรวจจับ replay/ผิด session เฉพาะกรณีที่ยังมี state เดิมใน storage
        if (expectedRandomState && expectedRandomState !== randomFromState) {
          throw new Error(
            "State ของ ThaID ไม่ตรงกับ session เดิม กรุณาเริ่มเข้าสู่ระบบใหม่"
          );
        }

        const res = await fetch(`${API_BASE}/auth/thaid/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            thaid_code: thaidCode,
            state: callbackState,
          }),
        });
        const data = await res.json();

        if (!res.ok || !data?.ok || !data?.token) {
          throw new Error(data?.error || data?.message || "ThaID exchange failed");
        }

        login(data.token, data.user);
        sessionStorage.removeItem("thaid_oauth_state");
        sessionStorage.removeItem("thaid_oauth_state_expires_at");
        localStorage.removeItem("thaid_oauth_state");
        localStorage.removeItem("thaid_oauth_state_expires_at");
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate("/");
      } catch (err) {
        console.error("ThaID login failed:", err);
        setError(err.message || "เข้าสู่ระบบด้วย ThaID ไม่สำเร็จ");
        window.history.replaceState({}, document.title, window.location.pathname);
      } finally {
        setLoading(false);
      }
    };

    processThaiIdCallback();
  }, [login, navigate]);

  useEffect(() => {
    if (hasProcessedThaiIdCallback.current) return;

    const params = new URLSearchParams(window.location.search);
    const psruNextCode = (params.get("psrunext_code") || "").trim();
    const callbackState = (params.get("state") || "").trim();
    const psruNextError = (params.get("psrunext_error") || "").trim();
    if (psruNextError) {
      hasProcessedThaiIdCallback.current = true;
      setError(`PSRU NEXT login failed: ${psruNextError}`);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    if (!psruNextCode || !callbackState) return;

    hasProcessedThaiIdCallback.current = true;

    const processPsruNextCallback = async () => {
      setError("");
      setLoading(true);
      try {
        if (!API_BASE) {
          throw new Error("ยังไม่ได้ตั้งค่า REACT_APP_API_BASE");
        }

        const res = await fetch(`${API_BASE}/auth/psrunext/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            psrunext_code: psruNextCode,
            state: callbackState,
          }),
        });
        const data = await res.json();
        if (!res.ok || !data?.ok || !data?.token) {
          throw new Error(
            data?.error || data?.message || "PSRU NEXT exchange failed"
          );
        }

        login(data.token, data.user);
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate("/");
      } catch (err) {
        console.error("PSRU NEXT login failed:", err);
        setError(err.message || "เข้าสู่ระบบด้วย PSRU NEXT ไม่สำเร็จ");
        window.history.replaceState({}, document.title, window.location.pathname);
      } finally {
        setLoading(false);
      }
    };

    processPsruNextCallback();
  }, [login, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();

      if (res.ok && data.ok && data.token) {
        login(data.token, data.user);
        navigate("/");
      } else {
        setError(
          data.error || data.message || "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง"
        );
      }
    } catch (err) {
      console.error("Login failed:", err);
      setError("ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-slate-50 font-sans selection:bg-blue-500/30">

      {/* 🔹 Dynamic Animated Background */}
      <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-500/30 blur-[120px] pointer-events-none animate-[pulse_6s_ease-in-out_infinite]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-purple-500/30 blur-[120px] pointer-events-none animate-[pulse_8s_ease-in-out_infinite_2s]"></div>
      <div className="absolute top-[20%] left-[40%] w-[500px] h-[500px] rounded-full bg-cyan-400/30 blur-[120px] pointer-events-none animate-[pulse_7s_ease-in-out_infinite_4s]"></div>
      <div className="absolute bottom-[20%] left-[10%] w-[400px] h-[400px] rounded-full bg-pink-400/20 blur-[120px] pointer-events-none animate-[pulse_9s_ease-in-out_infinite_3s]"></div>

      {/* 🔹 Subtle Grid Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>

      {/* 🔹 Glass Card */}
      <div className="relative z-10 w-full max-w-[420px] mx-4">
        {/* Outer Glow */}
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 via-purple-500/20 to-cyan-500/20 blur-2xl rounded-[2.5rem] transform scale-105 -z-10"></div>
        
        <div className="relative bg-white/70 backdrop-blur-2xl border border-white/60 shadow-2xl shadow-blue-500/10 rounded-[2.5rem] p-8 md:p-12 overflow-hidden">
          {/* Decorative top edge highlight */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
          
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-10">
            <div className="relative group cursor-default">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-cyan-600 rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
              <div className="relative w-20 h-20 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl shadow-blue-500/10 flex items-center justify-center transform group-hover:scale-105 transition-transform duration-500 ease-out overflow-hidden">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="w-full h-full object-cover"
                >
                  <source src={`${process.env.PUBLIC_URL}/avatar2.mp4`} type="video/mp4" />
                </video>
              </div>
              <div className="absolute -right-2 -top-2 bg-white/90 backdrop-blur-sm rounded-full p-1.5 shadow-sm">
                <Sparkles size={14} className="text-amber-400 fill-amber-400" />
              </div>
            </div>

            <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-slate-800">
              PSRU AI Hub
            </h1>
            <p className="mt-2 text-sm text-slate-500 font-medium">
              เข้าสู่ระบบเพื่อใช้งานพื้นที่ทำงานอัจฉริยะ
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {error && (
              <div className="p-4 rounded-2xl bg-red-50/80 border border-red-100 text-red-600 text-xs font-medium text-center animate-in fade-in slide-in-from-top-2">
                {error}
              </div>
            )}

            <div className="space-y-4">
              {/* Username Input */}
              <div className={`group relative transition-all duration-300 ${focusedInput === 'username' ? 'scale-[1.02]' : ''}`}>
                <div className={`absolute inset-0 bg-gradient-to-r from-blue-200 to-cyan-200 rounded-2xl blur opacity-0 transition-opacity duration-300 ${focusedInput === 'username' ? 'opacity-50' : ''}`}></div>
                <div className="relative bg-white/80 backdrop-blur-md rounded-2xl shadow-sm border border-white/50 transition-colors duration-300">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User size={20} className={`transition-colors duration-300 ${focusedInput === 'username' ? 'text-blue-500' : 'text-slate-400'}`} />
                  </div>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onFocus={() => setFocusedInput('username')}
                    onBlur={() => setFocusedInput(null)}
                    placeholder="บัญชี IMIS / ชื่อผู้ใช้งาน"
                    className="w-full pl-12 pr-4 py-4 bg-transparent rounded-2xl text-slate-800 placeholder:text-slate-500 text-sm font-medium focus:outline-none"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className={`group relative transition-all duration-300 ${focusedInput === 'password' ? 'scale-[1.02]' : ''}`}>
                <div className={`absolute inset-0 bg-gradient-to-r from-blue-200 to-cyan-200 rounded-2xl blur opacity-0 transition-opacity duration-300 ${focusedInput === 'password' ? 'opacity-50' : ''}`}></div>
                <div className="relative bg-white/80 backdrop-blur-md rounded-2xl shadow-sm border border-white/50 transition-colors duration-300">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock size={20} className={`transition-colors duration-300 ${focusedInput === 'password' ? 'text-blue-500' : 'text-slate-400'}`} />
                  </div>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedInput('password')}
                    onBlur={() => setFocusedInput(null)}
                    placeholder="รหัสผ่าน"
                    className="w-full pl-12 pr-4 py-4 bg-transparent rounded-2xl text-slate-800 placeholder:text-slate-500 text-sm font-medium focus:outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="pt-2 space-y-3">
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold text-sm
                        bg-gradient-to-r from-blue-600 via-violet-600 to-cyan-600 bg-[length:200%_auto] hover:bg-[position:right_center]
                        active:scale-[0.98] transition-all duration-500
                        shadow-lg shadow-blue-500/30 hover:shadow-violet-500/40 overflow-hidden"
              >
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>เข้าสู่ระบบ</span>
                    <ArrowRight size={18} className="opacity-70 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
              <div className="flex justify-end">
                <a
                  href="https://imis.psru.ac.th/main/forget_php/index.php"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold text-blue-600 hover:text-violet-600 hover:underline underline-offset-2 transition-colors"
                >
                  ลืมรหัสผ่าน?
                </a>
              </div>

              <div className="pt-1">
                <div className="relative flex items-center py-1.5">
                  <div className="flex-grow border-t border-slate-200/80"></div>
                  <span className="flex-shrink-0 mx-4 text-[11px] text-slate-400 font-medium">
                    หรือ
                  </span>
                  <div className="flex-grow border-t border-slate-200/80"></div>
                </div>

                <label className="mt-2 mb-2.5 block text-[11px] font-semibold tracking-wide text-slate-500 text-center">
                  เข้าสู่ระบบด้วย Digital ID
                </label>

                {shouldHideDigitalIdButtons ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-center">
                    <p className="text-xs font-semibold text-amber-700">
                      โหมดพัฒนา (Development Mode)
                    </p>
                    <p className="mt-1 text-[11px] text-amber-600">
                      ซ่อนปุ่มเข้าสู่ระบบ ThaID และ PSRU NEXT ชั่วคราว
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {[
                      {
                        id: "thaid",
                        imageSrc: "ThaID.png",
                        alt: "ThaID",
                        label: "แอปพลิเคชัน ThaID",
                      },
                      {
                        id: "psru-next",
                        imageSrc: "LogoPSRUNEXT.png",
                        alt: "PSRU NEXT",
                        label: "แอปพลิเคชัน PSRU NEXT",
                      },
                    ].map((app) => (
                      <button
                        key={app.id}
                        type="button"
                        id={app.id}
                        onClick={
                          app.id === "thaid"
                            ? handleThaiIdLogin
                            : app.id === "psru-next"
                              ? handlePsruNextLogin
                              : undefined
                        }
                        className="group relative w-full flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur-md px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-all duration-300 hover:border-blue-200 hover:bg-white hover:shadow-md active:scale-[0.98]"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-100 bg-white shadow-sm">
                          <img
                            src={`${process.env.PUBLIC_URL}/${app.imageSrc}`}
                            alt={app.alt}
                            className="h-6 w-6 object-contain"
                          />
                        </span>
                        <span className="text-left">{app.label}</span>
                        <span className="ml-auto h-2.5 w-2.5 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 opacity-60 transition-opacity duration-300 group-hover:opacity-100"></span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Supported Models Icons */}
            <div className="pt-6 mt-2 border-t border-slate-100/80">
              <p className="text-[10px] text-slate-400 font-medium text-center uppercase tracking-wider mb-4">
                Powered by World-Class AI Models
              </p>
              <div className="flex items-center justify-center gap-5">
                {[
                  { name: "ChatGPT", src: "chatgpt-icon.png" },
                  { name: "Claude", src: "claude-ai-icon.png" },
                  { name: "Perplexity", src: "perplexity-ai-icon.png" },
                  { name: "Gemini", src: "gemini-icon.png" },
                ].map((model) => (
                  <div key={model.name} className="group relative" title={model.name}>
                    <div className="w-8 h-8 rounded-xl bg-white border border-slate-100 shadow-sm flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:shadow-md group-hover:border-blue-100">
                      <img
                        src={`${process.env.PUBLIC_URL}/${model.src}`}
                        alt={model.name}
                        className="w-5 h-5 object-contain"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-[11px] text-slate-500 font-semibold">
              สถาบันเทคโนโลยีดิจิทัล @2026
            </p>
          </div>
        </div>

        {/* Bottom Floating Text */}
        {/* <div className="text-center mt-6">
             <p className="text-xs text-slate-500/80 font-medium">
                พบปัญหาการใช้งาน? <a href="#" className="text-blue-600 hover:text-blue-500 transition-colors">ติดต่อฝ่ายสนับสนุน</a>
             </p>
        </div> */}
      </div>
    </div>
  );
}
